class ProductCard extends HTMLElement {
  constructor() {
    super();

    // Core elements
    this.form = this.querySelector('[data-product-form]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.decreaseBtn = this.querySelector('[data-quantity-decrease]');
    this.increaseBtn = this.querySelector('[data-quantity-increase]');

    // Purchase type (one-time vs subscribe)
    this.purchaseOptions = this.querySelectorAll('[data-purchase-option]');
    this.purchaseRadios = this.querySelectorAll('[data-purchase-radio]');
    this.frequencyWrapper = this.querySelector('[data-frequency-wrapper]');
    this.sellingPlanSelect = this.querySelector('[data-selling-plan-select]');

    // Variant
    this.variantSelect = this.querySelector('[data-variant-select]');
    this.hiddenVariantInput = this.querySelector('input[name="id"]');

    // Add to cart button / price display
    this.atcButton = this.querySelector('[data-add-to-cart]');
    this.atcLabel = this.querySelector('.product-card__atc-label');
    this.atcPrice = this.querySelector('[data-atc-price]');
    this.priceOnetime = this.querySelector('[data-price-onetime]');
    this.priceSubscribe = this.querySelector('[data-price-subscribe]');
    this.quantityMessage = this.querySelector('[data-quantity-message]');

    // Variant data rendered server-side by Liquid (see snippet notes below)
    this.variantsData = this.parseVariantsJSON();
    this.currentVariant = this.getVariantById(
      this.variantSelect ? this.variantSelect.value : this.hiddenVariantInput?.value
    );

    // Shared theme elements outside this component
    this.elements = {
      toaster: document.querySelector('ce-toaster'),
    };

    this.init();
  }

  // ---------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------

  init() {
    if (this.decreaseBtn) {
      this.decreaseBtn.addEventListener('click', () => this.updateQuantity(-1));
    }
    if (this.increaseBtn) {
      this.increaseBtn.addEventListener('click', () => this.updateQuantity(1));
    }
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.clampQuantity());
    }

    this.purchaseOptions.forEach((option) => {
      option.addEventListener('click', () => this.selectPurchaseType(option));
    });

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => this.handleVariantChange());
    }
    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.addEventListener('change', () => this.syncPurchaseTypeUI());
    }

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    // Pre-select "subscribe" on first load if a subscription is available
    // for the current variant. Only runs once here at init — variant
    // switches later on do NOT force-reset the user's chosen purchase type
    // (see handleVariantChange below).
    this.setDefaultPurchaseType();
    this.syncPurchaseTypeUI();
  }

  parseVariantsJSON() {
    const script = this.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent).variants || [];
    } catch (err) {
      console.error('Product card: failed to parse variants JSON', err);
      return [];
    }
  }

  getVariantById(id) {
    if (!id) return this.variantsData[0];
    return this.variantsData.find((v) => String(v.id) === String(id)) || this.variantsData[0];
  }

  // ---------------------------------------------------------------------
  // Quantity
  // ---------------------------------------------------------------------

  updateQuantity(delta) {
    const current = parseInt(this.quantityInput.value, 10) || 1;
    this.quantityInput.value = Math.max(1, current + delta);
    this.clampQuantity();
  }

  clampQuantity() {
    let value = parseInt(this.quantityInput.value, 10);
    if (isNaN(value) || value < 1) value = 1;

    const max = this.getMaxAvailableQuantity();
    if (max !== null && value > max) {
      value = max;

      const message =
        max > 0
          ? `Only ${max} left in stock — quantity set to ${max}.`
          : 'This item is out of stock.';

      this.showQuantityMessage(message);

      // Only fire the toast the moment we cross into the over-limit state,
      // not on every subsequent call (e.g. repeated clicks/change events
      // while already sitting at the cap).
      if (!this._stockLimitReached) {
        this._stockLimitReached = true;
        this.showStockToast();
      }
    } else {
      this.hideQuantityMessage();
      this._stockLimitReached = false;
    }

    this.quantityInput.value = value;
  }

  showStockToast() {
    const toaster = this.elements.toaster;
    if (!toaster || typeof toaster.makeToast !== 'function') return;
    toaster.makeToast('danger', Themu.t.products.buy_buttons.error_message, 7000);
  }

  // Returns the max quantity allowed for the current variant, or null if
  // there's no cap (untracked inventory, or policy allows overselling).
  getMaxAvailableQuantity() {
    const variant = this.currentVariant;
    if (!variant) return null;
    if (variant.inventory_management !== 'shopify') return null;
    if (variant.inventory_policy === 'continue') return null;
    return Math.max(0, variant.inventory_quantity ?? 0);
  }

  showQuantityMessage(text) {
    if (!this.quantityMessage) return;
    this.quantityMessage.textContent = text;
    this.quantityMessage.hidden = false;
  }

  hideQuantityMessage() {
    if (!this.quantityMessage) return;
    this.quantityMessage.hidden = true;
    this.quantityMessage.textContent = '';
  }

  // ---------------------------------------------------------------------
  // Purchase type (one-time / subscribe)
  // ---------------------------------------------------------------------

  selectPurchaseType(option) {
    const radio = option.querySelector('[data-purchase-radio]');
    radio.checked = true;
    this.syncPurchaseTypeUI();
  }

  getSelectedPurchaseType() {
    let selected = 'onetime';
    this.purchaseOptions.forEach((option) => {
      const radio = option.querySelector('[data-purchase-radio]');
      option.classList.toggle('product-card__purchase-option--active', radio.checked);
      if (radio.checked) selected = option.dataset.purchaseOption;
    });
    return selected;
  }

  // If the current variant has an available subscription (selling plan
  // allocations), pre-select the "subscribe" radio/option. Falls back to
  // whatever is already checked (usually "onetime") if no subscription
  // exists for this variant.
  setDefaultPurchaseType() {
    const variant = this.currentVariant || this.variantsData[0];
    if (!variant) return;

    const hasSubscription = (variant.selling_plan_allocations || []).length > 0;
    if (!hasSubscription) return;

    const subscribeOption = Array.from(this.purchaseOptions).find(
      (option) => option.dataset.purchaseOption === 'onetime'
    );
    if (!subscribeOption) return;

    const radio = subscribeOption.querySelector('[data-purchase-radio]');
    if (radio) radio.checked = true;
  }

  syncPurchaseTypeUI() {
    const variant = this.currentVariant || this.variantsData[0];
    if (!variant) return;

    const type = this.getSelectedPurchaseType();
    const isSubscribe = type === 'subscribe';

    if (this.frequencyWrapper) {
      this.frequencyWrapper.style.display = isSubscribe ? 'block' : 'none';
      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.disabled = !isSubscribe;
        this.sellingPlanSelect.name = isSubscribe ? 'selling_plan' : '';
      }
    }

    if (this.priceOnetime) {
      this.priceOnetime.textContent = this.formatMoney(variant.price);
    }

    let activePriceCents = variant.price;
    let planUnavailable = false;

    if (isSubscribe && this.sellingPlanSelect) {
      const planId = this.sellingPlanSelect.value;
      const allocation = (variant.selling_plan_allocations || []).find(
        (a) => String(a.selling_plan_id) === String(planId)
      );

      if (allocation) {
        activePriceCents = allocation.price;
        if (this.priceSubscribe) {
          this.priceSubscribe.textContent = this.formatMoney(allocation.price);
        }
      } else {
        planUnavailable = true;
        if (this.priceSubscribe) this.priceSubscribe.textContent = 'Unavailable';
      }
    }

    if (this.atcPrice) this.atcPrice.textContent = this.formatMoney(activePriceCents);

    const maxQuantity = this.getMaxAvailableQuantity();
    const outOfStock = maxQuantity !== null && maxQuantity < 1;
    const isAvailable = variant.available && !planUnavailable && !outOfStock;

    this.atcButton.disabled = !isAvailable;
    this.atcLabel.textContent = !variant.available || outOfStock
      ? 'Sold Out'
      : (planUnavailable ? 'Unavailable' : 'Add to Cart');
  }

  // ---------------------------------------------------------------------
  // Variant change (no network request — reads embedded JSON)
  // ---------------------------------------------------------------------

  handleVariantChange() {
    this.currentVariant = this.getVariantById(this.variantSelect.value);
    if (this.hiddenVariantInput) this.hiddenVariantInput.value = this.currentVariant.id;
    this.clampQuantity();
    // Note: setDefaultPurchaseType() is intentionally NOT called here, so
    // switching variants doesn't override a purchase type the user already
    // picked manually. Remove this comment and add the call back in if you
    // want each variant switch to re-default to "subscribe" when available.
    this.syncPurchaseTypeUI();
  }

  // ---------------------------------------------------------------------
  // Money formatting
  // ---------------------------------------------------------------------

  formatMoney(cents) {
    // Prefer the theme's real money formatting if it's available globally.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }

  // ---------------------------------------------------------------------
  // Add to cart (jQuery AJAX)
  // ---------------------------------------------------------------------

  // Sections re-rendered server-side and returned alongside the cart response,
  // so the drawer/notification can update from this single request instead of
  // firing a second fetch against /cart or /?section_id=...
  static CART_SECTIONS = ['cart-data', 'cart-drawer', 'cart-drawer-notification'];

  handleSubmit(event) {
    event.preventDefault();
    if (this.atcButton.disabled) return;

    if (typeof $ === 'undefined' && typeof jQuery === 'undefined') {
      console.error('Product card: jQuery is not loaded on this page.');
      return;
    }
    const $$ = window.jQuery || window.$;

    // Final safety check in case stock changed since the last input/change
    // event (e.g. rapid clicking before clampQuantity's message rendered).
    this.clampQuantity();
    if (this.atcButton.disabled) return;

    const formData = new FormData(this.form);
    if (this.getSelectedPurchaseType() === 'onetime') {
      formData.delete('selling_plan');
    }

    // Ask Shopify to render these sections fresh and return their HTML
    // in the same response as the cart/add result.
    formData.append('sections', ProductCard.CART_SECTIONS.join(','));
    formData.append('sections_url', window.location.pathname);

    this.setLoading(true);

    const url = `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    $$.ajax({
      url: url,
      method: 'POST',
      data: formData,
      processData: false, // don't let jQuery serialize FormData
      contentType: false, // let the browser set multipart/form-data + boundary
      dataType: 'json',
      headers: { Accept: 'application/json' },
    })
      .done((responseBody) => {
        this.setLoading(false, true);

        // Notify the rest of the theme (cart drawer, header count, etc.) with
        // the freshly rendered section HTML.
        document.dispatchEvent(
          new CustomEvent('Themu:cart:add', {
            bubbles: true,
            detail: responseBody.sections,
          })
        );

        const cartDrawer = document.querySelector('ce-cart-drawer');
        cartDrawer?.handleClickButtonOpen();
      })
      .fail((jqXHR) => {
        let message = jqXHR.statusText;
        try {
          const data = JSON.parse(jqXHR.responseText);
          message = data.description || data.message || message;
        } catch (err) {
          // response wasn't JSON — fall back to statusText
        }
        console.error('Add to cart error:', message);
        // Covers the rare race condition where stock changed between page
        // load and submit, and the client-side check in clampQuantity()
        // didn't have fresh enough data to catch it.
        this.showStockToast();
        this.setLoading(false, false, true);
      });
  }

  setLoading(isLoading, success = false, error = false) {
    this.atcButton.disabled = isLoading || !this.currentVariant?.available;
    this.atcButton.classList.toggle('is-loading', isLoading);

    if (isLoading) {
      this.atcLabel.textContent = 'Adding...';
    } else if (success) {
      this.atcLabel.textContent = 'Added';
      setTimeout(() => this.syncPurchaseTypeUI(), 1200);
    } else if (error) {
      this.atcLabel.textContent = 'Error — try again';
      setTimeout(() => this.syncPurchaseTypeUI(), 1500);
    }
  }
}

customElements.define('product-card', ProductCard);